import { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { useModalEditarSolicitud } from "./useModalEditarSolicitud";
import { Modal } from "react-bootstrap";
import { InputFecha } from "../../../components/formComponents/InputFecha";
import { EditarCambioUnidad } from "./components/EditarCambioUnidad";
import { EditarCambioContrato } from "./components/EditarCambioContrato";
import { EditarDesvinculacion } from "./components/EditarDesvinculacion";
import ErrorInput from "../../../components/ui/ErrorInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faTimes, faUser, faIdCard, faBuilding, faBriefcase, faEdit, faInfoCircle, faUserTie } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import moment from "moment";

export const FIELD_LABEL_STYLE = {
  fontSize: "0.7rem",
  letterSpacing: "0.5px",
  minHeight: "20px",
  color: "#64748b",
  fontWeight: "700",
  textTransform: "uppercase",
  display: "block",
  marginBottom: "0.25rem",
};

const InfoBadge = ({ icon, label, value }) => (
  <div className="d-flex align-items-center bg-white p-2 rounded-3 border" style={{ minWidth: "200px", flex: "1 1 200px" }}>
    <div className="me-2 rounded-2 p-2 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#eef6fc", color: "#2a77b9", width: "36px", height: "36px" }}>
      <FontAwesomeIcon icon={icon} size="sm" />
    </div>
    <div className="text-truncate">
      <div className="text-uppercase fw-bold text-muted" style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>
        {label}
      </div>
      <div className="text-dark fw-bold text-truncate" style={{ fontSize: "0.85rem" }} title={value || "---"}>
        {value || "---"}
      </div>
    </div>
  </div>
);

InfoBadge.propTypes = {
  icon: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

const LabelVal = ({ label, val, isTextArea = false }) => (
  <div>
    <label style={FIELD_LABEL_STYLE}>
      {label}
    </label>
    <div
      className={`rounded px-3 py-2 fw-semibold text-dark ${isTextArea ? "overflow-auto" : "text-truncate d-flex align-items-center"}`}
      style={{
        height: isTextArea ? "80px" : "38px",
        backgroundColor: "#f0f7ff",
        border: "1px solid #cbe2f7",
        fontSize: "0.9rem",
      }}
      title={val || "---"}
    >
      {val || "---"}
    </div>
  </div>
);

LabelVal.propTypes = {
  label: PropTypes.string.isRequired,
  val: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isTextArea: PropTypes.bool,
};

const FormSkeleton = () => (
  <div className="d-flex flex-column gap-3 py-2">
    <style>{`
      @keyframes skeletonPulse {
        0% { opacity: 0.5; background-color: #e2e8f0; }
        50% { opacity: 1; background-color: #cbd5e1; }
        100% { opacity: 0.5; background-color: #e2e8f0; }
      }
      .skeleton-box {
        animation: skeletonPulse 1.5s ease-in-out infinite;
        border-radius: 6px;
      }
    `}</style>
    <div>
      <div className="skeleton-box mb-2" style={{ width: "40%", height: "16px" }} />
      <div className="skeleton-box" style={{ width: "100%", height: "38px" }} />
    </div>
    <div>
      <div className="skeleton-box mb-2" style={{ width: "55%", height: "16px" }} />
      <div className="skeleton-box" style={{ width: "100%", height: "38px" }} />
    </div>
    <div>
      <div className="skeleton-box mb-2" style={{ width: "50%", height: "16px" }} />
      <div className="skeleton-box" style={{ width: "100%", height: "38px" }} />
    </div>
  </div>
);

const normCode = (s) => String(s || "").trim().replace(/^0+/, "");

const getPosDescripcion = (pos, fallbackNombre = "") => {
  if (!pos) return fallbackNombre;
  const desc =
    pos.MOES_DESCRIPCION_POSICION ||
    pos.MOES_DESCRIPCION ||
    pos.DESCRIPCION_POSICION ||
    pos.descripcion_posicion ||
    pos.nombre_cargo ||
    pos.nombre ||
    pos.descripcion ||
    fallbackNombre ||
    "";
  return String(desc).trim();
};

const formatCargoLabel = (pos, fallbackNombre = "") => {
  const cod = pos?.MOES_COD_POSICION || pos?.cod || "";
  const desc = getPosDescripcion(pos, fallbackNombre);
  if (cod && desc) {
    if (desc.startsWith(cod)) return desc;
    return `${cod} - ${desc}`;
  }
  if (cod && !desc) return cod;
  return desc || cod || "---";
};

const formatGroupLabel = (data) => (
  <div
    className="d-flex align-items-center justify-content-between px-2 py-1 rounded-2"
    style={{
      backgroundColor: "#eef6fc",
      color: "#14406e",
      fontWeight: "700",
      fontSize: "0.76rem",
      letterSpacing: "0.4px",
      border: "1px solid #cbd5e1",
      textTransform: "uppercase",
    }}
  >
    <span className="text-truncate" title={data.label}>
      {data.label}
    </span>
    {data.options && (
      <span
        className="badge rounded-pill ms-2"
        style={{
          backgroundColor: "#2a77b9",
          color: "#ffffff",
          fontSize: "0.62rem",
          fontWeight: "600",
        }}
      >
        {data.options.length} {data.options.length === 1 ? "vacante" : "vacantes"}
      </span>
    )}
  </div>
);

const buildCargoOptions = (posicionesLaborales, solicitudFallback = null) => {
  if (!posicionesLaborales || typeof posicionesLaborales !== "object") return [];
  return Object.keys(posicionesLaborales).map((grupo) => ({
    label: grupo,
    options: (posicionesLaborales[grupo] || []).map((pos) => ({
      label: formatCargoLabel(pos, solicitudFallback?.nombre_nuevo_cargo),
      value: {
        ...pos,
        MOES_DESCRIPCION_POSICION: getPosDescripcion(pos, solicitudFallback?.nombre_nuevo_cargo),
      },
    })),
  }));
};

const findSelectedCargo = (val, cargoOptions, solicitud = null) => {
  if (!val && !solicitud?.cod_sap_nuevo_cargo) return null;

  let targetCod = "";
  let targetDesc = "";

  if (typeof val === "object" && val !== null) {
    targetCod = String(val.MOES_COD_POSICION || val.cod || "");
    targetDesc = getPosDescripcion(val, solicitud?.nombre_nuevo_cargo);
  } else if (typeof val === "string" && val.trim()) {
    try {
      if (val.trim().startsWith("{")) {
        const parsed = JSON.parse(val);
        targetCod = String(parsed.MOES_COD_POSICION || parsed.cod || "");
        targetDesc = getPosDescripcion(parsed, solicitud?.nombre_nuevo_cargo);
      } else {
        targetCod = val.trim();
      }
    } catch (e) {
      targetCod = val.trim();
    }
  }

  if (!targetCod && solicitud?.cod_sap_nuevo_cargo) {
    targetCod = String(solicitud.cod_sap_nuevo_cargo).trim();
  }
  if (!targetDesc && solicitud?.nombre_nuevo_cargo) {
    targetDesc = String(solicitud.nombre_nuevo_cargo).trim();
  }

  const normTarget = normCode(targetCod);

  if (normTarget) {
    for (const group of cargoOptions) {
      const found = group.options.find(
        (opt) => normCode(opt.value?.MOES_COD_POSICION || opt.value?.cod) === normTarget
      );
      if (found) {
        const finalDesc = getPosDescripcion(found.value, targetDesc);
        return {
          ...found,
          label: formatCargoLabel({ ...found.value, MOES_DESCRIPCION_POSICION: finalDesc }, targetDesc),
        };
      }
    }
  }

  if (targetCod || targetDesc) {
    const fallbackLabel = formatCargoLabel({ MOES_COD_POSICION: targetCod, MOES_DESCRIPCION_POSICION: targetDesc });
    return {
      label: fallbackLabel,
      value: {
        MOES_COD_POSICION: targetCod,
        MOES_DESCRIPCION_POSICION: targetDesc,
      },
    };
  }

  return null;
};

const formatCargoSolicitado = (solicitud) => {
  const cod = solicitud?.cod_sap_nuevo_cargo;
  const nombre = solicitud?.nombre_nuevo_cargo;
  if (cod && nombre) {
    if (String(nombre).includes(String(cod))) return nombre;
    return `${cod} - ${nombre}`;
  }
  return nombre || cod || "---";
};

const formatAdminOrigen = (solicitud) => {
  if (!solicitud) return "---";
  if (solicitud.cod_sap_administrador) {
    return solicitud.nombre_administrador
      ? `${solicitud.cod_sap_administrador} - ${solicitud.nombre_administrador}`
      : solicitud.cod_sap_administrador;
  }
  if (solicitud.persona_solicitante) {
    return `${solicitud.persona_solicitante.nombre || ""} ${solicitud.persona_solicitante.apellido_pat || ""}`.trim();
  }
  return solicitud.nombre_solicitante || solicitud.solicitante || solicitud.usuario_creacion || "---";
};

const formatAdminDestino = (solicitud) => {
  if (!solicitud) return "---";
  if (solicitud.cod_sap_administrador_destino) {
    return solicitud.nombre_administrador_destino
      ? `${solicitud.cod_sap_administrador_destino} - ${solicitud.nombre_administrador_destino}`
      : solicitud.cod_sap_administrador_destino;
  }
  return solicitud.cod_sap_admin_destino || solicitud.nombre_administrador_destino || "---";
};

const formatEstatusContrato = (solicitud) => {
  if (!solicitud) return "---";
  if (["01", 1].includes(solicitud.tipo_cambio_contrato)) return "Cambiar a indefinido";
  if (["02", 2].includes(solicitud.tipo_cambio_contrato)) return "Extensión de plazo fijo";
  return solicitud.tipo_cambio_contrato || "---";
};

const formatFechaTexto = (fechaStr) => {
  if (!fechaStr) return "---";
  const m = moment(fechaStr, ["YYYY-MM-DD", "DD/MM/YYYY", "YYYY/MM/DD"]);
  return m.isValid() ? m.format("DD/MM/YYYY") : fechaStr;
};

const formatPlanActual = (planHorario) => {
  if (!planHorario) return "---";
  if (planHorario.includes("|")) {
    return planHorario.split("|")[1];
  }
  return planHorario;
};

export const ModalEditarSolicitudForm = ({ solicitud, hideModal, onSuccess }) => {
  const { formik, posicionesLaborales, cargando: cargandoPosiciones } = useModalEditarSolicitud(solicitud, hideModal, onSuccess);
  const [cargandoSub, setCargandoSub] = useState(() => [1, 2, 3, 4, 5].includes(Number(solicitud?.id_tipo_solicitud)));
  const [initialLoadingDone, setInitialLoadingDone] = useState(false);

  const handleSubLoading = useCallback((isLoading) => {
    setCargandoSub(Boolean(isLoading));
  }, []);

  useEffect(() => {
    const requiresSub = [1, 2, 3, 4, 5].includes(Number(solicitud?.id_tipo_solicitud));
    setCargandoSub(requiresSub);
    setInitialLoadingDone(false);
  }, [solicitud?.id, solicitud?.id_tipo_solicitud]);

  useEffect(() => {
    if (!cargandoPosiciones && !cargandoSub && !initialLoadingDone) {
      setInitialLoadingDone(true);
    }
  }, [cargandoPosiciones, cargandoSub, initialLoadingDone]);

  if (!solicitud) return null;

  const isFormLoading = !initialLoadingDone;
  const cargoOptions = buildCargoOptions(posicionesLaborales, solicitud);
  const getSelectedCargo = () => findSelectedCargo(formik.values.cod_sap_nuevo_cargo, cargoOptions, solicitud);

  const tipo = Number(solicitud.id_tipo_solicitud);
  const nombreColaborador = `${solicitud.persona?.nombre || ""} ${solicitud.persona?.apellido_pat || ""} ${solicitud.persona?.apellido_mat || ""}`.trim();
  const cargoActualNombre = solicitud.nombre_cargo_actual ? solicitud.nombre_cargo_actual.split("|")[1] : "---";

  const cargoSolicitadoNombre = formatCargoSolicitado(solicitud);
  const adminOrigenNombre = formatAdminOrigen(solicitud);
  const adminDestinoNombre = formatAdminDestino(solicitud);
  const estatusContratoTexto = formatEstatusContrato(solicitud);
  const unidadActualNombre = solicitud.descripcion_unidad_temporis || "---";
  const planActualNombre = formatPlanActual(solicitud.nuevo_plan_horario);

  const fechaEfectivaTexto = formatFechaTexto(solicitud.fecha_inicio_cambio);
  const fechaRenovacionTexto = formatFechaTexto(solicitud.fecha_renovacion_contrato);

  return (
    <div className="d-flex flex-column gap-3">
      {/* Banner Superior: Información Fija del Colaborador y Administradores */}
      <div className="card rounded-3 shadow-none" style={{ border: "1px solid #cbd5e1", borderRadius: "12px", overflow: "hidden", backgroundColor: "#f8fafc", boxShadow: "none" }}>
        <div className="card-body p-3 d-flex flex-column gap-2">
          {tipo === 3 ? (
            <>
              <div className="d-flex flex-wrap gap-2">
                <InfoBadge icon={faUser} label="Colaborador" value={nombreColaborador} />
                <InfoBadge icon={faIdCard} label="RUT" value={solicitud.persona?.identificacion} />
                <InfoBadge icon={faBriefcase} label="Cargo Actual" value={cargoActualNombre} />
              </div>
              <div className="d-flex flex-wrap gap-2 pt-2 border-top" style={{ borderColor: "#cbd5e1" }}>
                <InfoBadge icon={faBuilding} label="Instalación Origen" value={solicitud.instalacion_actual} />
                <InfoBadge icon={faUserTie} label="Admin. Origen" value={adminOrigenNombre} />
                <InfoBadge icon={faBuilding} label="Instalación Destino" value={solicitud.nombre_instalacion_destino} />
                <InfoBadge icon={faUserTie} label="Admin. Destino" value={adminDestinoNombre} />
              </div>
            </>
          ) : (
            <>
              <div className="d-flex flex-wrap gap-2">
                <InfoBadge icon={faUser} label="Colaborador" value={nombreColaborador} />
                <InfoBadge icon={faIdCard} label="RUT" value={solicitud.persona?.identificacion} />
                <InfoBadge icon={faBriefcase} label="Cargo Actual" value={cargoActualNombre} />
              </div>
              <div className="d-flex flex-wrap gap-2 pt-2 border-top" style={{ borderColor: "#cbd5e1" }}>
                <InfoBadge icon={faBuilding} label="Instalación" value={solicitud.instalacion_actual} />
                <InfoBadge icon={faUserTie} label="Admin. Solicitante" value={adminOrigenNombre} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* 2 Cards Separados: Información Actual vs Datos a Modificar */}
      <div className="row g-3">
        {/* Card Izquierdo: Información Almacenada */}
        <div className="col-12 col-lg-5">
          <div className="card h-100 shadow-none" style={{ border: "1px solid #cbd5e1", borderRadius: "12px", overflow: "hidden", backgroundColor: "#ffffff", boxShadow: "none" }}>
            <div className="card-header bg-light py-3 border-bottom d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faInfoCircle} style={{ color: "#2a77b9" }} />
              <h6 className="fw-bold m-0" style={{ color: "#14406e" }}>
                Información Almacenada
              </h6>
            </div>
            <div className="card-body p-3 d-flex flex-column gap-3">
              {![5].includes(tipo) && (
                <LabelVal
                  label="Fecha efectiva de cambio"
                  val={fechaEfectivaTexto}
                />
              )}

              {[1, 3].includes(tipo) && (
                <LabelVal label="Cargo SAP" val={cargoSolicitadoNombre} />
              )}

              {[1, 2, 3].includes(tipo) && (
                <>
                  <LabelVal label="Unidad organizativa - Cargo (Temporis)" val={unidadActualNombre} />
                  <LabelVal label="Plan horario" val={planActualNombre} />
                </>
              )}


              {[4].includes(tipo) && (
                <>
                  <LabelVal label="Estatus contrato solicitado" val={estatusContratoTexto} />
                  {["02", "2", 2].includes(solicitud.tipo_cambio_contrato) && (
                    <LabelVal
                      label="Fecha extensión de plazo fijo"
                      val={fechaRenovacionTexto}
                    />
                  )}
                </>
              )}

              {[5].includes(tipo) && (
                <>
                  <LabelVal label="Fecha de baja de colaborador" val={fechaEfectivaTexto || "Sin fecha de baja"} />
                  <LabelVal label="Causal de desvinculación" val={solicitud.clausula_despido?.nombre || "No especifica"} />
                  <LabelVal label="Motivo de la desvinculación" val={solicitud.motivo_desvinculacion} isTextArea={true} />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Card Derecho: Datos a Modificar */}
        <div className="col-12 col-lg-7">
          <div className="card h-100 d-flex flex-column justify-content-between shadow-none" style={{ border: "1px solid #cbd5e1", borderRadius: "12px", overflow: "hidden", backgroundColor: "#ffffff", boxShadow: "none" }}>
            <div>
              <div className="card-header bg-light py-3 border-bottom d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faEdit} style={{ color: "#2a77b9" }} />
                <h6 className="fw-bold m-0" style={{ color: "#14406e" }}>
                  Datos a Modificar
                </h6>
              </div>
              <div className="card-body p-3">
                {isFormLoading && <FormSkeleton />}

                <div className={isFormLoading ? "d-none" : ""}>
                  <form id="form-editar-solicitud" onSubmit={formik.handleSubmit} className="d-flex flex-column gap-3">
                    {![5].includes(tipo) && (
                      <InputFecha formik={formik} label="NUEVA FECHA EFECTIVA DE CAMBIO *" id="fecha_inicio_cambio" labelStyle={FIELD_LABEL_STYLE} />
                    )}

                    {[1, 3].includes(tipo) && (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label htmlFor="cod_sap_nuevo_cargo" style={{ ...FIELD_LABEL_STYLE, marginBottom: 0 }}>
                            NUEVO CARGO SAP <span className="text-danger">*</span>
                          </label>
                          {formik.errors.cod_sap_nuevo_cargo && formik.touched.cod_sap_nuevo_cargo && (
                            <ErrorInput touched={formik.touched.cod_sap_nuevo_cargo} error={formik.errors.cod_sap_nuevo_cargo} />
                          )}
                        </div>
                        <Select
                          id="cod_sap_nuevo_cargo"
                          name="cod_sap_nuevo_cargo"
                          className="selectDos"
                          classNamePrefix="limpiarSelect"
                          placeholder="Seleccione nueva posición..."
                          isClearable={true}
                          menuPlacement="auto"
                          menuPosition="fixed"
                          maxMenuHeight={220}
                          options={cargoOptions}
                          value={getSelectedCargo()}
                          formatGroupLabel={formatGroupLabel}
                          onChange={(e) => {
                            formik.setFieldValue("cod_sap_nuevo_cargo", e ? JSON.stringify(e.value) : "");
                            formik.setFieldValue("nueva_unidad_organizativa", "");
                            formik.setFieldValue("descripcion_unidad_temporis", "");
                            formik.setFieldValue("nuevo_plan_horario", "");
                          }}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            menu: (base) => ({ ...base, zIndex: 9999 }),
                            groupHeading: (base) => ({
                              ...base,
                              textTransform: "none",
                              padding: "2px 4px",
                              margin: "2px 0 4px 0",
                              color: "inherit",
                              fontWeight: "normal",
                            }),
                          }}
                        />
                      </div>
                    )}

                    {[1, 2, 3].includes(tipo) && <EditarCambioUnidad formik={formik} solicitud={solicitud} tipo={tipo} onLoadingChange={handleSubLoading} />}
                    {[4].includes(tipo) && <EditarCambioContrato formik={formik} solicitud={solicitud} onLoadingChange={handleSubLoading} />}
                    {[5].includes(tipo) && <EditarDesvinculacion formik={formik} onLoadingChange={handleSubLoading} />}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botones de Acción del Modal */}
      <div className="d-flex justify-content-end gap-2 pt-3 pb-3 mb-2 border-top" style={{ borderColor: "#e2e8f0" }}>
        <button
          type="submit"
          form="form-editar-solicitud"
          className="btn px-4 py-2 rounded-pill fw-bold d-flex align-items-center text-white"
          style={{ backgroundColor: "#2a77b9", borderColor: "#2a77b9" }}
          disabled={isFormLoading || formik.isSubmitting}
        >
          <FontAwesomeIcon icon={faSave} className="me-2" /> Actualizar
        </button>
        <button
          type="button"
          className="btn btn-secondary px-4 py-2 rounded-pill fw-bold d-flex align-items-center"
          onClick={hideModal}
        >
          <FontAwesomeIcon icon={faTimes} className="me-2" /> Cancelar
        </button>
      </div>
    </div>
  );
};

ModalEditarSolicitudForm.propTypes = {
  solicitud: PropTypes.object,
  hideModal: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export const ModalEditarSolicitud = ({ show, hideModal, solicitud, onSuccess }) => {
  if (!show || !solicitud) return null;
  const nombreTipo = solicitud.tipo_solicitud_cambio_contraactual?.nombre || "Cambio Contractual";

  return (
    <Modal show={show} onHide={hideModal} size="xl" centered backdrop="static" keyboard={false}>
      <Modal.Header
        closeButton
        closeVariant="white"
        className="pb-3 pt-3"
        style={{
          backgroundColor: "#2a77b9",
          borderRadius: "20px 20px 0 0",
          borderBottom: "none",
        }}
      >
        <h5 className="m-0" style={{ color: "#ffffff", fontWeight: "600" }}>
          Editar Solicitud #{solicitud.id} - {nombreTipo}
        </h5>
      </Modal.Header>
      <Modal.Body className="p-3 pb-4" style={{ backgroundColor: "#ffffff", borderRadius: "0 0 20px 20px" }}>
        <ModalEditarSolicitudForm solicitud={solicitud} hideModal={hideModal} onSuccess={onSuccess} />
      </Modal.Body>
    </Modal>
  );
};

ModalEditarSolicitud.propTypes = {
  show: PropTypes.bool.isRequired,
  hideModal: PropTypes.func.isRequired,
  solicitud: PropTypes.object,
  onSuccess: PropTypes.func,
};
